'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { pushToDataLayer, withPageContext } from '@/app/lib/analytics/dataLayer'
import { useOptionalChromeAnalytics } from './ChromeAnalyticsContext'
import type {
  AddToCartParams,
  ClickEventParams,
  FilterChangeParams,
  PageContext,
  PaginationParams,
  SearchEventParams,
  SearchNoResultsParams,
  SelectItemParams,
  SelectPromotionParams,
  UiInteractionParams,
  ViewItemListParams,
  ViewItemParams,
  ViewMoreParams,
  ViewPromotionParams,
  ViewSearchResultsParams,
} from '@/app/lib/analytics/types'

interface PageAnalyticsValue {
  /** Contexto de página vigente, tal como se empujó en el `page_view`. */
  readonly context: PageContext
  /** Empuja un `click` con contexto de página + los 12 params fijos. */
  readonly trackClick: (params: ClickEventParams) => void
  /** Empuja un `ui_interaction` con contexto de página + los 8 params fijos. */
  readonly trackUiInteraction: (params: UiInteractionParams) => void
  /** Empuja un `filter_change` con contexto de página (cambio de filtro en PLP/SERP). */
  readonly trackFilterChange: (params: FilterChangeParams) => void
  /** Empuja un `view_item_list` (impresión de un listado de productos en viewport). */
  readonly trackViewItemList: (params: ViewItemListParams) => void
  /** Empuja un `select_item` (click en una card del listado). */
  readonly trackSelectItem: (params: SelectItemParams) => void
  /** Empuja un `view_promotion` (impresión de una pieza de carrusel/banner en viewport). */
  readonly trackViewPromotion: (params: ViewPromotionParams) => void
  /** Empuja un `select_promotion` (click en una pieza de carrusel/banner). */
  readonly trackSelectPromotion: (params: SelectPromotionParams) => void
  /** Empuja un `view_item` (carga de la PDP, tras el `page_view`). */
  readonly trackViewItem: (params: ViewItemParams) => void
  /**
   * Empuja un `add_to_cart` (CTA "Comprar" — ÚLTIMO EVENTO EN SCOPE, guía 01
   * §2/§3). El llamador es responsable de disparar este push ANTES de dejar
   * navegar al checkout externo (no hay `preventDefault` aquí).
   */
  readonly trackAddToCart: (params: AddToCartParams) => void
  /** Empuja un `pagination` (navegación a otra página del listado). */
  readonly trackPagination: (params: PaginationParams) => void
  /** Empuja un `view_more` (carga de más ítems sin cambiar de página). */
  readonly trackViewMore: (params: ViewMoreParams) => void
  /**
   * Empuja un `view_search_results` (SERP con resultados renderizados, tras el
   * `page_view`). Condicional con `trackSearchNoResults`: nunca los dos a la
   * vez (`Resultados de búsqueda.csv`).
   */
  readonly trackViewSearchResults: (params: ViewSearchResultsParams) => void
  /** Empuja un `search_no_results` (SERP sin resultados para el término buscado). */
  readonly trackSearchNoResults: (params: SearchNoResultsParams) => void
  /** Empuja un `search` (submit del buscador — Enter/lupa, header o `SearchBand`). */
  readonly trackSearch: (params: SearchEventParams) => void
}

// `null` por defecto: los componentes de hoja usan `useOptionalPageAnalytics`
// para funcionar también fuera del provider (p. ej. en tests del organismo
// compartido `AlphaListingPage`). Las páginas usan `usePageAnalytics` (estricto).
const PageAnalyticsContext = createContext<PageAnalyticsValue | null>(null)

interface PageAnalyticsProviderProps {
  /**
   * Contexto de la página actual. Cada `page.tsx` (Server Component) lo calcula
   * y lo pasa aquí; este provider (cliente) dispara el `page_view` y lo expone a
   * las hojas para que TODO push posterior lleve el contexto (guía 02 §3).
   */
  readonly context: PageContext
  readonly children: ReactNode
}

// `useLayoutEffect` avisa al renderizar en servidor; en SSR no se ejecuta ningún
// efecto, así que basta con degradar a `useEffect` allí.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * `PageAnalyticsProvider` — dispara `page_view` en cada carga/navegación y
 * expone el contexto de página a los eventos de hoja.
 *
 * Client Component: el tracking es interactividad de cliente y no debe vivir en
 * el `page.tsx`/layout de servidor (arquitectura · boundaries RSC). En SPA, al
 * navegar (p. ej. cambio de provincia en recintos) el `page.tsx` se re-renderiza
 * con un `context` nuevo → el efecto vuelve a disparar `page_view` con la
 * jerarquía correcta, sin reiniciar el dataLayer (guía 02 §9).
 */
export function PageAnalyticsProvider({ context, children }: PageAnalyticsProviderProps) {
  // Evita el doble `page_view` del doble-montaje de React StrictMode en dev y
  // de cualquier re-render que no cambie realmente de página (guía 08: doble
  // disparo). La clave identifica la página de forma estable.
  const lastPageViewKey = useRef<string | undefined>(undefined)
  const pageViewKey = `${context.page_type}|${context.page_subtype}|${context.page_hierarchy}`
  const chrome = useOptionalChromeAnalytics()

  // Layout effect: React ejecuta los efectos hijo → padre, así que con
  // `useEffect` los eventos "en carga" de las hojas (p. ej. `view_item`) se
  // empujaban ANTES que este `page_view`. Los layout effects de todo el árbol
  // corren antes que cualquier `useEffect`, garantizando `page_view` primero.
  useIsomorphicLayoutEffect(() => {
    if (lastPageViewKey.current === pageViewKey) return
    lastPageViewKey.current = pageViewKey
    pushToDataLayer(withPageContext(context, { event: 'page_view' }))
    // `context` se identifica por `pageViewKey`; no re-disparar por otras props.
  }, [pageViewKey])

  // Reporta el contexto de página vigente al `ChromeAnalyticsProvider` (si el
  // layout raíz lo monta): el chrome (header/footer) es HERMANO de `{children}`,
  // no descendiente de este provider, y necesita conocer la página activa para
  // que sus propios eventos (p. ej. el buscador del header) lleven contexto.
  // Keyed por `pageViewKey`, igual que el push de `page_view` arriba, para no
  // reportar en cada render — solo cuando la página realmente cambia.
  useEffect(() => {
    chrome?.setPageContext(context)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageViewKey])

  const trackClick = useCallback(
    (params: ClickEventParams) =>
      pushToDataLayer(withPageContext(context, { event: 'click', ...params })),
    [context],
  )

  const trackUiInteraction = useCallback(
    (params: UiInteractionParams) =>
      pushToDataLayer(withPageContext(context, { event: 'ui_interaction', ...params })),
    [context],
  )

  const trackFilterChange = useCallback(
    (params: FilterChangeParams) =>
      pushToDataLayer(withPageContext(context, { event: 'filter_change', ...params })),
    [context],
  )

  const trackViewItemList = useCallback(
    (params: ViewItemListParams) =>
      pushToDataLayer(withPageContext(context, { event: 'view_item_list', ...params })),
    [context],
  )

  const trackSelectItem = useCallback(
    (params: SelectItemParams) =>
      pushToDataLayer(withPageContext(context, { event: 'select_item', ...params })),
    [context],
  )

  const trackViewPromotion = useCallback(
    (params: ViewPromotionParams) =>
      pushToDataLayer(withPageContext(context, { event: 'view_promotion', ...params })),
    [context],
  )

  const trackSelectPromotion = useCallback(
    (params: SelectPromotionParams) =>
      pushToDataLayer(withPageContext(context, { event: 'select_promotion', ...params })),
    [context],
  )

  const trackViewItem = useCallback(
    (params: ViewItemParams) =>
      pushToDataLayer(withPageContext(context, { event: 'view_item', ...params })),
    [context],
  )

  const trackAddToCart = useCallback(
    (params: AddToCartParams) =>
      pushToDataLayer(withPageContext(context, { event: 'add_to_cart', ...params })),
    [context],
  )

  const trackPagination = useCallback(
    (params: PaginationParams) =>
      pushToDataLayer(withPageContext(context, { event: 'pagination', ...params })),
    [context],
  )

  const trackViewMore = useCallback(
    (params: ViewMoreParams) =>
      pushToDataLayer(withPageContext(context, { event: 'view_more', ...params })),
    [context],
  )

  const trackViewSearchResults = useCallback(
    (params: ViewSearchResultsParams) =>
      pushToDataLayer(withPageContext(context, { event: 'view_search_results', ...params })),
    [context],
  )

  const trackSearchNoResults = useCallback(
    (params: SearchNoResultsParams) =>
      pushToDataLayer(withPageContext(context, { event: 'search_no_results', ...params })),
    [context],
  )

  const trackSearch = useCallback(
    (params: SearchEventParams) =>
      pushToDataLayer(withPageContext(context, { event: 'search', ...params })),
    [context],
  )

  const value = useMemo<PageAnalyticsValue>(
    () => ({
      context,
      trackClick,
      trackUiInteraction,
      trackFilterChange,
      trackViewItemList,
      trackSelectItem,
      trackViewPromotion,
      trackSelectPromotion,
      trackViewItem,
      trackAddToCart,
      trackPagination,
      trackViewMore,
      trackViewSearchResults,
      trackSearchNoResults,
      trackSearch,
    }),
    [
      context,
      trackClick,
      trackUiInteraction,
      trackFilterChange,
      trackViewItemList,
      trackSelectItem,
      trackViewPromotion,
      trackSelectPromotion,
      trackViewItem,
      trackAddToCart,
      trackPagination,
      trackViewMore,
      trackViewSearchResults,
      trackSearchNoResults,
      trackSearch,
    ],
  )

  return <PageAnalyticsContext.Provider value={value}>{children}</PageAnalyticsContext.Provider>
}

/** Estricto: falla fuera del provider. Para páginas que garantizan el wrapper. */
export function usePageAnalytics(): PageAnalyticsValue {
  const ctx = useContext(PageAnalyticsContext)
  if (!ctx) throw new Error('usePageAnalytics debe usarse dentro de <PageAnalyticsProvider>.')
  return ctx
}

/**
 * Tolerante: devuelve `null` fuera del provider. Para hojas reutilizables (p. ej.
 * `AlphaEntryLink`) que también se renderizan en tests o contextos sin tracking.
 */
export function useOptionalPageAnalytics(): PageAnalyticsValue | null {
  return useContext(PageAnalyticsContext)
}
